import type {
  AdditionalSpaceObservingSession,
  RoomObservingSession,
  VisitCounselView,
} from '@/entities/visit-counsel/model/visit-counsel.types';
import { hasObservingSessionMovingItemsConfirmed } from '@/entities/visit-counsel/model/observing-session-moving-items';

export type ObservingSessionKind = 'room' | 'additional';

export interface PollCounselUntilObservingOptions {
  /**
   * true 이면 짐 + 영상까지 저장한 케이스 — 폴링 종료 조건을
   * `status === 'COMPLETED'` 또는 `session.video` 필드가 채워졌을 때로 좁힌다.
   *
   * 기본 false (`hasObservingSessionMovingItemsConfirmed` 와 동일한 종료 조건):
   * `STARTED` 도 통과 → 영상 미반영 상태에서도 호출자가 picker 갱신을 시작할 수 있다.
   *
   * 영상까지 함께 저장한 흐름은 picker 의 done 배지가 즉시 보여야 하므로
   * `true` 로 호출해 backend 의 COMPLETED 전환까지 명시적으로 기다린다.
   */
  expectComplete?: boolean;
  /** 최대 폴링 시간(ms). 기본 10초 */
  maxMs?: number;
  /** 폴링 간격(ms). 기본 400ms */
  intervalMs?: number;
}

const DEFAULT_MAX_MS = 10_000;
const DEFAULT_INTERVAL_MS = 400;

type DetailLike = { counsel?: VisitCounselView | null } | undefined;

function pickSession(
  detail: DetailLike,
  sessionId: string,
  kind: ObservingSessionKind
): RoomObservingSession | AdditionalSpaceObservingSession | undefined {
  const c = detail?.counsel;
  if (kind === 'room') return c?.sessions?.roomObserving?.find((s) => s.id === sessionId);
  return c?.sessions?.additionalSpaceObserving?.find((s) => s.id === sessionId);
}

function isSessionReady(
  session: RoomObservingSession | AdditionalSpaceObservingSession | undefined,
  expectComplete: boolean
): boolean {
  if (!session) return false;
  if (expectComplete) {
    // 영상까지 저장한 케이스 — backend 가 세션을 COMPLETED 로 전환하거나
    // video 필드를 응답에 반영할 때까지 대기. 둘 중 하나라도 만족하면
    // picker 의 done 계산(`status === 'COMPLETED' || (hasVideo && hasItems)`)
    // 이 즉시 done 으로 평가된다.
    return session.status === 'COMPLETED' || !!session.video;
  }
  return hasObservingSessionMovingItemsConfirmed(session);
}

/**
 * 짐/영상 저장 API 호출 후 backend 상태가 반영되기까지 폴링한다.
 *
 * - 호출자는 매 iteration 의 `refetch()` 결과를 SWR 캐시 업데이트로 사용할 수 있다.
 * - 종료 조건은 `expectComplete` 로 결정된다 (위 옵션 주석 참조).
 * - timeout(`maxMs`) 안에 조건이 만족되지 않으면 마지막으로 받은 counsel 을
 *   그대로 반환한다 — 호출자 측에서 stale data 라도 표시하도록 fail-soft.
 */
export async function pollCounselUntilObservingMovingConfirmed(
  refetch: () => Promise<DetailLike>,
  sessionId: string,
  kind: ObservingSessionKind,
  options: PollCounselUntilObservingOptions = {}
): Promise<VisitCounselView | null> {
  const {
    expectComplete = false,
    maxMs = DEFAULT_MAX_MS,
    intervalMs = DEFAULT_INTERVAL_MS,
  } = options;

  const start = Date.now();
  let result = await refetch();

  while (Date.now() - start < maxMs) {
    if (isSessionReady(pickSession(result, sessionId, kind), expectComplete)) {
      return result?.counsel ?? null;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
    result = await refetch();
  }

  return result?.counsel ?? null;
}
